import { API_BASE_URL } from '../config/apiBase';
import { ensureCsrfToken, isPlausibleCsrfToken, readCsrfCookie } from './csrf';

const API_BASE = API_BASE_URL;

async function apiRequest(path, options = {}) {
  const headers = {
    Accept: 'application/json',
    ...(options.headers || {}),
  };
  if (options.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  const method = (options.method || 'GET').toUpperCase();
  if (method !== 'GET' && method !== 'HEAD') {
    let csrf = readCsrfCookie();
    if (!isPlausibleCsrfToken(csrf)) {
      csrf = await ensureCsrfToken();
    }
    headers['X-CSRFToken'] = csrf;
  }
  if (options.tenantId) {
    headers['X-Tenant-Id'] = options.tenantId;
  }

  const { tenantId, ...fetchOptions } = options;

  const response = await fetch(`${API_BASE}/v1${path}`, {
    credentials: 'include',
    ...fetchOptions,
    headers,
  });

  let data = null;
  const text = await response.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch (err) {
      data = { error: { code: 'invalid_json', message: text } };
    }
  }

  if (!response.ok) {
    const error = new Error(
      (data && data.error && data.error.message) || `Request failed (${response.status})`
    );
    error.status = response.status;
    error.code = data && data.error ? data.error.code : 'http_error';
    error.details = data && data.error ? data.error.details : null;
    error.payload = data;
    throw error;
  }
  return data;
}

function withTenant(tenantId, options = {}) {
  return { ...options, tenantId };
}

export const accountApi = {
  getAccount: (tenantId) => apiRequest('/account/', withTenant(tenantId)),
  updateAccount: (body, tenantId) =>
    apiRequest('/account/', withTenant(tenantId, { method: 'PATCH', body: JSON.stringify(body) })),
  changePassword: (body) =>
    apiRequest('/account/change-password/', { method: 'POST', body: JSON.stringify(body) }),
  requestPasswordReset: (body) =>
    apiRequest('/account/password-reset/', { method: 'POST', body: JSON.stringify(body) }),
  confirmPasswordReset: (body) =>
    apiRequest('/account/password-reset/confirm/', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  listTenants: () => apiRequest('/partners/'),
  createPersonalTenant: () =>
    apiRequest('/partners/', { method: 'POST', body: JSON.stringify({}) }),
  createTenant: (body) =>
    apiRequest('/partners/', { method: 'POST', body: JSON.stringify(body) }),
  createPartner: (body) =>
    apiRequest('/partners/', { method: 'POST', body: JSON.stringify(body) }),
  getTenant: (tenantId) => apiRequest(`/partners/${tenantId}/`, withTenant(tenantId)),
  getPartner: (partnerId) => apiRequest(`/partners/${partnerId}/`, withTenant(partnerId)),
  getPartnerOverview: (partnerId) =>
    apiRequest(`/partners/${partnerId}/overview/`, withTenant(partnerId)),
  getPartnerUsage: (partnerId) =>
    apiRequest(`/partners/${partnerId}/usage/`, withTenant(partnerId)),
  getPartnerBilling: (partnerId) =>
    apiRequest(`/partners/${partnerId}/billing/`, withTenant(partnerId)),
  startBillingCheckout: (partnerId, body) =>
    apiRequest(
      `/partners/${partnerId}/billing/checkout/`,
      withTenant(partnerId, { method: 'POST', body: JSON.stringify(body) })
    ),
  openBillingPortal: (partnerId) =>
    apiRequest(
      `/partners/${partnerId}/billing/portal/`,
      withTenant(partnerId, { method: 'POST', body: JSON.stringify({}) })
    ),
  changeBillingPlan: (partnerId, body) =>
    apiRequest(
      `/partners/${partnerId}/billing/change-plan/`,
      withTenant(partnerId, { method: 'POST', body: JSON.stringify(body) })
    ),
  cancelBilling: (partnerId, body) =>
    apiRequest(
      `/partners/${partnerId}/billing/cancel/`,
      withTenant(partnerId, { method: 'POST', body: JSON.stringify(body || {}) })
    ),
  resumeBilling: (partnerId) =>
    apiRequest(
      `/partners/${partnerId}/billing/resume/`,
      withTenant(partnerId, { method: 'POST', body: JSON.stringify({}) })
    ),
  completeMockCheckout: (partnerId, body) =>
    apiRequest(
      `/partners/${partnerId}/billing/mock/complete-checkout/`,
      withTenant(partnerId, { method: 'POST', body: JSON.stringify(body) })
    ),
  setMockBillingScenario: (partnerId, body) =>
    apiRequest(
      `/partners/${partnerId}/billing/mock/set-scenario/`,
      withTenant(partnerId, { method: 'POST', body: JSON.stringify(body) })
    ),
  resetMockBilling: (partnerId) =>
    apiRequest(
      `/partners/${partnerId}/billing/mock/reset/`,
      withTenant(partnerId, { method: 'POST', body: JSON.stringify({}) })
    ),
  updateTenant: (tenantId, body) =>
    apiRequest(
      `/partners/${tenantId}/`,
      withTenant(tenantId, { method: 'PATCH', body: JSON.stringify(body) })
    ),
  updatePartner: (partnerId, body) =>
    apiRequest(
      `/partners/${partnerId}/`,
      withTenant(partnerId, { method: 'PATCH', body: JSON.stringify(body) })
    ),

  listMembers: (tenantId) =>
    apiRequest(`/partners/${tenantId}/members/`, withTenant(tenantId)),
  listInvitations: (tenantId) =>
    apiRequest(`/partners/${tenantId}/invitations/`, withTenant(tenantId)),
  createInvitation: (tenantId, body) =>
    apiRequest(
      `/partners/${tenantId}/invitations/`,
      withTenant(tenantId, { method: 'POST', body: JSON.stringify(body) })
    ),
  revokeInvitation: (tenantId, invitationId) =>
    apiRequest(
      `/partners/${tenantId}/invitations/${invitationId}/revoke/`,
      withTenant(tenantId, { method: 'POST', body: JSON.stringify({}) })
    ),
  resendInvitation: (tenantId, invitationId) =>
    apiRequest(
      `/partners/${tenantId}/invitations/${invitationId}/resend/`,
      withTenant(tenantId, { method: 'POST', body: JSON.stringify({}) })
    ),
  copyPartnerInvitationLink: (tenantId, invitationId) =>
    apiRequest(
      `/partners/${tenantId}/invitations/${invitationId}/copy-link/`,
      withTenant(tenantId, { method: 'POST', body: JSON.stringify({}) })
    ),
  acceptInvitation: (token) =>
    apiRequest('/invitations/accept/', { method: 'POST', body: JSON.stringify({ token }) }),
  validateInvitation: (token) =>
    apiRequest(`/invitations/validate/?token=${encodeURIComponent(token)}`),
  registerFromInvitation: (body) =>
    apiRequest('/invitations/register/', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  getTenantRoleCatalog: (tenantId) =>
    apiRequest('/partner-roles/', withTenant(tenantId)),
  updateMemberRole: (tenantId, membershipId, role) =>
    apiRequest(
      `/partners/${tenantId}/members/${membershipId}/role/`,
      withTenant(tenantId, { method: 'POST', body: JSON.stringify({ role }) })
    ),
  removeMember: (tenantId, membershipId) =>
    apiRequest(
      `/partners/${tenantId}/members/${membershipId}/remove/`,
      withTenant(tenantId, { method: 'POST', body: JSON.stringify({}) })
    ),

  listApiKeys: (tenantId) =>
    apiRequest(`/partners/${tenantId}/api-keys/`, withTenant(tenantId)),
  createApiKey: (tenantId, body) =>
    apiRequest(
      `/partners/${tenantId}/api-keys/`,
      withTenant(tenantId, { method: 'POST', body: JSON.stringify(body) })
    ),
  revokeApiKey: (tenantId, keyId) =>
    apiRequest(
      `/partners/${tenantId}/api-keys/${keyId}/revoke/`,
      withTenant(tenantId, { method: 'POST', body: JSON.stringify({}) })
    ),
  rotateApiKey: (tenantId, keyId) =>
    apiRequest(
      `/partners/${tenantId}/api-keys/${keyId}/rotate/`,
      withTenant(tenantId, { method: 'POST', body: JSON.stringify({}) })
    ),

  listTenantAudit: (tenantId, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiRequest(
      `/partners/${tenantId}/audit/${qs ? `?${qs}` : ''}`,
      withTenant(tenantId)
    );
  },

  getPlatformOverview: () => apiRequest('/platform/overview/'),
  listPlatformTenants: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiRequest(`/platform/partners/${qs ? `?${qs}` : ''}`);
  },
  listPlatformPartners: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiRequest(`/platform/partners/${qs ? `?${qs}` : ''}`);
  },
  getPlatformPartner: (partnerId) => apiRequest(`/platform/partners/${partnerId}/`),
  getPlatformPartnerEntitlements: (partnerId) =>
    apiRequest(`/platform/partners/${partnerId}/entitlements/`),
  updatePlatformPartnerEntitlements: (partnerId, body) =>
    apiRequest(`/platform/partners/${partnerId}/entitlements/`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  resendPlatformPartnerInvitation: (partnerId, invitationId) =>
    apiRequest(`/platform/partners/${partnerId}/invitations/${invitationId}/resend/`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  revokePlatformPartnerInvitation: (partnerId, invitationId) =>
    apiRequest(`/platform/partners/${partnerId}/invitations/${invitationId}/revoke/`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  suspendPlatformTenant: (tenantId, body) =>
    apiRequest(`/platform/partners/${tenantId}/status/`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  suspendPlatformPartner: (partnerId, body) =>
    apiRequest(`/platform/partners/${partnerId}/status/`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  createPlatformPartner: (body) =>
    apiRequest('/platform/partners/create/', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  onboardPlatformPartner: (body) =>
    apiRequest('/platform/partners/onboard/', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getPlatformPartnerOnboardingOptions: () =>
    apiRequest('/platform/partners/onboarding-options/'),
  lookupPlatformPartnerOwner: (body) =>
    apiRequest('/platform/partners/owner-lookup/', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  listPlatformUsers: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiRequest(`/platform/users/${qs ? `?${qs}` : ''}`);
  },
  getPlatformUser: (userId) => apiRequest(`/platform/users/${userId}/`),
  listPlatformJobs: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiRequest(`/platform/jobs/${qs ? `?${qs}` : ''}`);
  },
  getWorkerSetup: () => apiRequest('/platform/workers/setup/'),
  createPlatformWorker: (body) => apiRequest('/platform/workers/', { method: 'POST', body: JSON.stringify(body) }),
  getPlatformWorker: (id) => apiRequest(`/platform/workers/${id}/`),
  platformWorkerAction: (id, action) => apiRequest(`/platform/workers/${id}/${action}/`, { method: 'POST', body: JSON.stringify({}) }),
  listPlatformWorkers: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiRequest(`/platform/workers/${qs ? `?${qs}` : ''}`);
  },
  listPlatformPolicies: () => apiRequest('/platform/policies/'),
  listPlatformAudit: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiRequest(`/platform/audit/${qs ? `?${qs}` : ''}`);
  },

  getPlatformRoleCatalog: () => apiRequest('/platform/roles/'),
  listPlatformRoleAssignments: () => apiRequest('/platform/role-assignments/'),
  grantPlatformRole: (body) =>
    apiRequest('/platform/role-assignments/grant/', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  revokePlatformRole: (body) =>
    apiRequest('/platform/role-assignments/revoke/', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

export default accountApi;
